import { createContext, useContext, ParentComponent, JSX, createEffect } from 'solid-js';
import { authStore, login as authLogin, logout as authLogout } from './authStore';
import { authApi } from '../services/api';
import socketService from '../services/socket';
import { config } from '../config/environment';
import type { User } from '../types';

// Auth Context Type
interface AuthContextType {
  token: () => string | null;
  user: () => User | null;
  isAuthenticated: () => boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType>();

// Auth Provider Component
export const AuthProvider: ParentComponent<{ children: JSX.Element }> = (props) => {
  // Initialize socket when authenticated
  createEffect(() => {
    const isAuth = authStore.isAuthenticated();

    if (isAuth) {
      // Initialize socket connection with team code from config
      // The team code is available from environment/config after authentication
      const teamCode = config.team.code;
      socketService.initialize(teamCode);
    } else {
      // Disconnect socket when logged out
      socketService.disconnect();
    }
  });

  // Login function
  const login = async (password: string) => {
    try {
      const response = await authApi.login(password);

      // Store token and refresh token
      authLogin(response.token, undefined, response.refreshToken);
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call backend logout endpoint to revoke token
      await authApi.logout();
    } catch (error) {
      // Continue with local logout even if API call fails
    }

    // Clear local auth state
    authLogout();
    socketService.disconnect();

    // Redirect to login
    window.location.href = '/login';
  };

  const contextValue: AuthContextType = {
    token: authStore.token,
    user: authStore.user,
    isAuthenticated: authStore.isAuthenticated,
    login,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
